import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { node } from 'prop-types';
import { useLocation } from 'react-router-dom';

import featureToggle, { DAY_TRADING } from '@config/featureToggle';
import { API_SUCCESS_CODE, DEFAULT_GET_RESPONSE } from '@config/constanta';

import {
  getDtParamCashTransfer,
  getDtParamBuySell,
  getDtMultiplierList,
  getCheckStockDtEnabled,
} from '@services/api/stock/dayTrading/config';
import { getDtAccountStatus, getDtTradelimit } from '@services/api/stock/dayTrading/account';
import { getDtPortfolioList } from '@services/api/stock/dayTrading/portfolio';
import { postDtBuy, postDtSell } from '@services/api/stock/dayTrading/trading';
import { useQueryParams } from '@hooks/useQueryParams';

export const DayTradingContext = React.createContext();

const DayTradingProvider = ({ children }) => {
  const isMounted = useRef(true);
  const { queryParams } = useQueryParams();
  const featDayTrading = featureToggle.some((feat) => feat.feature === DAY_TRADING && feat.toggle);
  const [dtPorto, setDTPorto] = useState(DEFAULT_GET_RESPONSE);
  const [loadingPorto, setLoadingPorto] = useState(true);
  const location = useLocation();
  const includeDtPorto =
    queryParams.get('tab') === 'dayTrading' || location.pathname.includes('/saham/');

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const fetchDayTradingPortoList = useCallback(() => {
    // requested only if feature toggle true
    if (featDayTrading) {
      setLoadingPorto(true);
      getDtPortfolioList()
        .then((response) => {
          if (!isMounted.current) {
            return;
          }
          if (response?.err_code === API_SUCCESS_CODE) {
            setDTPorto(response);
          }
          setLoadingPorto(false);
        })
        .catch((err) => {
          console.error(err);
          setLoadingPorto(false);
        });
    }
  }, [featDayTrading, getDtPortfolioList]);

  const [loadingStatus, setLoadingStatus] = useState(true);
  const [dtActive, setDtActive] = useState(false);
  const [dtInProcess, setDtInProcess] = useState(false);
  const [dtSuspendBuy, setDtSuspendBuy] = useState(false);
  const [dtSuspendSell, setDtSuspendSell] = useState(false);
  const [dtTradingHourEndBuy, setDtTradingHourEndBuy] = useState(false);
  const [dtTradingHourEndSell, setDtTradingHourEndSell] = useState(false);

  const fetchDTStatus = useCallback(() => {
    if (featDayTrading) {
      setLoadingStatus(true);
      getDtAccountStatus()
        .then((response) => {
          if (!isMounted.current) {
            return;
          }
          // Status whether account has KYC-ed for Day Trading
          setDtActive(response?.result?.odt_status === 'ACTIVE');
          setDtInProcess(response?.result?.odt_status === 'IN_PROCESS');
          // Status whether account is currently suspended for DT trading
          setDtSuspendBuy(response?.result?.trade_buy_status === 'SUSPEND_BUY');
          setDtSuspendSell(response?.result?.trade_sell_status === 'SUSPEND_SELL');
          // Status for DT trading hours
          setDtTradingHourEndBuy(response?.result?.trade_buy_status === 'FC_SUSPEND_BUY');
          setDtTradingHourEndSell(response?.result?.trade_sell_status === 'FC_SUSPEND_SELL');
          setLoadingStatus(false);
        })
        .catch((err) => {
          console.error('Error status ODT', err);
          setDtActive(false);
          setLoadingStatus(false);
        });
    }
  }, [featDayTrading, getDtAccountStatus]);

  const [loadingMultipliers, setLoadingMultipliers] = useState(false);
  const [dtMultipliers, setDtMultipliers] = useState({});

  const fetchDayTradingMultiplierList = useCallback(() => {
    if (featDayTrading && dtActive) {
      setLoadingMultipliers(true);
      getDtMultiplierList()
        .then((response) => {
          if (!isMounted.current) {
            return;
          }
          setDtMultipliers(response?.result);
          setLoadingMultipliers(false);
        })
        .catch((err) => {
          console.error(err);
          setLoadingMultipliers(false);
        });
    }
  }, [featDayTrading, dtActive, getDtMultiplierList]);

  const [loadingTrade, setLoadingTrade] = useState(false);
  const [dtTradeLimit, setDtTradeLimit] = useState({});
  const [isErrorFetchDTTradeLimit, setIsErrorFetchDTTradeLimit] = useState(false);

  const fetchDayTradingTradeLimit = useCallback(
    (code) => {
      if (featDayTrading && dtActive) {
        setLoadingTrade(true);
        getDtTradelimit(code)
          .then((response) => {
            if (!isMounted.current) {
              return;
            }
            setDtTradeLimit(response);
            setLoadingTrade(false);
            if (response?.err_code !== 'EC0000000') {
              setIsErrorFetchDTTradeLimit(true);
            }
          })
          .catch((err) => {
            console.error(err);
            setLoadingTrade(false);
            setIsErrorFetchDTTradeLimit(true);
          });
      }
    },
    [featDayTrading, dtActive, getDtTradelimit]
  );

  const [dtParamsCashTransfer, setDtParamsCashTransfer] = useState(false);
  const [dtParamsBuySell, setDtparamsBuySell] = useState({});

  const fetchDayTradingParamsCashTransfer = useCallback(() => {
    getDtParamCashTransfer()
      .then((response) => {
        if (!isMounted.current) {
          return;
        }
        setDtParamsCashTransfer(response?.result);
      })
      .catch((err) => console.error(err));
  }, [getDtParamCashTransfer]);

  const fetchDayTradingparamsBuySell = useCallback(() => {
    getDtParamBuySell()
      .then((response) => {
        if (!isMounted.current) {
          return;
        }
        setDtparamsBuySell(response?.result);
      })
      .catch((err) => console.error(err));
  }, [getDtParamBuySell]);

  useEffect(() => {
    let intervalPortoDt;
    // requested only if feature toggle true
    if (featDayTrading && dtActive) {
      fetchDayTradingPortoList();
    }
    if (featDayTrading && includeDtPorto && dtActive) {
      intervalPortoDt = setInterval(() => {
        fetchDayTradingPortoList();
      }, 5000);
    }
    return () => clearInterval(intervalPortoDt);
  }, [featDayTrading, includeDtPorto, dtActive]);

  useEffect(() => {
    if (featDayTrading) {
      fetchDTStatus();
    }
  }, [featDayTrading]);

  useEffect(() => {
    if (dtActive) {
      fetchDayTradingTradeLimit();
      fetchDayTradingMultiplierList();
    }
  }, [dtActive]);

  // TODO: Remove when PopUpBuySell is deprecated
  const onBuyDayTradingStock = useCallback((payload) => postDtBuy(payload), [postDtBuy]);
  const onSellDayTradingStock = useCallback((payload) => postDtSell(payload), [postDtSell]);
  const checkStockDtEligibility = useCallback(
    (payload) => getCheckStockDtEnabled(payload),
    [getCheckStockDtEnabled]
  );

  const valueProps = useMemo(
    () => ({
      loadingStatus,
      loadingMultipliers,
      loadingTrade,
      loadingPorto,
      dtActive,
      dtInProcess,
      dtSuspendBuy,
      dtSuspendSell,
      dtTradingHourEndBuy,
      dtTradingHourEndSell,
      dtMultipliers,
      dtPorto,
      fetchDTStatus,
      fetchDayTradingPortoList,
      dtTradeLimit,
      fetchDayTradingTradeLimit,
      dtParamsCashTransfer,
      fetchDayTradingParamsCashTransfer,
      dtParamsBuySell,
      fetchDayTradingparamsBuySell,
      onBuyDayTradingStock,
      onSellDayTradingStock,
      checkStockDtEligibility,
      isErrorFetchDTTradeLimit,
    }),
    [
      loadingStatus,
      loadingMultipliers,
      loadingTrade,
      loadingPorto,
      dtActive,
      dtSuspendBuy,
      dtSuspendSell,
      dtTradingHourEndBuy,
      dtTradingHourEndSell,
      dtMultipliers,
      dtPorto,
      dtInProcess,
      fetchDTStatus,
      fetchDayTradingPortoList,
      dtTradeLimit,
      fetchDayTradingTradeLimit,
      dtParamsCashTransfer,
      fetchDayTradingParamsCashTransfer,
      dtParamsBuySell,
      fetchDayTradingparamsBuySell,
      onBuyDayTradingStock,
      onSellDayTradingStock,
      checkStockDtEligibility,
      isErrorFetchDTTradeLimit,
    ]
  );

  return <DayTradingContext.Provider value={valueProps}>{children}</DayTradingContext.Provider>;
};

DayTradingProvider.propTypes = {
  children: node.isRequired,
};

export default DayTradingProvider;
